const VIDEO_FILE_REGEX = /\.(mp4|webm|ogg|mov|avi|m4v)(\?.*)?$/i;
const URL_REGEX = /(https?:\/\/[^\s<>"']+)/gi;
const VIDEO_TEXT_SKIP_TAGS = new Set([
    'IFRAME',
    'VIDEO',
    'SCRIPT',
    'STYLE',
    'A'
]);
const SEARCH_SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'VIDEO', 'MARK']);
const isVideoFile = (url) => VIDEO_FILE_REGEX.test(url);
const buildYoutubeEmbedUrl = (id) => id ? `https://www.youtube.com/embed/${id}` : null;
const getFirstPathSegment = (pathname) => pathname.split('/').find(Boolean) ?? null;
const getSegmentAfterPrefix = (pathname, prefix) => pathname.split(prefix)[1]?.split('/')[0] ?? null;
const getYoutubeEmbedUrl = (url) => {
    try {
        const parsed = new URL(url);
        const { hostname, pathname, searchParams } = parsed;
        if (hostname.includes('youtu.be')) {
            return buildYoutubeEmbedUrl(getFirstPathSegment(pathname));
        }
        if (!hostname.includes('youtube.com')) {
            return null;
        }
        if (pathname === '/watch') {
            return buildYoutubeEmbedUrl(searchParams.get('v'));
        }
        if (pathname.startsWith('/embed/')) {
            return url;
        }
        if (pathname.startsWith('/live/')) {
            return buildYoutubeEmbedUrl(getSegmentAfterPrefix(pathname, '/live/'));
        }
        if (pathname.startsWith('/shorts/')) {
            return buildYoutubeEmbedUrl(getSegmentAfterPrefix(pathname, '/shorts/'));
        }
        return null;
    }
    catch {
        return null;
    }
};
const createResponsiveWrapper = (doc) => {
    const wrapper = doc.createElement('div');
    wrapper.setAttribute('style', 'position: relative; width: 100%; padding-bottom: 56.25%; height: 0; margin: 16px 0; overflow: hidden; border-radius: 8px;');
    return wrapper;
};
const createIframeElement = (doc, src, title) => {
    const iframe = doc.createElement('iframe');
    iframe.src = src;
    iframe.title = title;
    iframe.loading = 'lazy';
    iframe.setAttribute('style', 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0; border-radius: 8px;');
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen');
    iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    return iframe;
};
const createYoutubeIframe = (doc, embedUrl) => {
    const wrapper = createResponsiveWrapper(doc);
    const iframe = createIframeElement(doc, embedUrl, 'YouTube video player');
    wrapper.appendChild(iframe);
    return wrapper;
};
const createVideoPlayer = (doc, url) => {
    const video = doc.createElement('video');
    video.src = url;
    video.controls = true;
    video.preload = 'metadata';
    video.setAttribute('style', 'width: 100%; max-width: 100%; margin: 16px 0; border-radius: 8px; display: block;');
    video.setAttribute('playsinline', 'true');
    video.setAttribute('controlsList', 'nodownload');
    return video;
};
const isSharePointHost = (hostname) => hostname.includes('sharepoint.com') ||
    hostname.includes('sharepoint-df.com');
const isLikelySharePointVideoUrl = (url) => {
    try {
        const { hostname, pathname, searchParams } = new URL(url);
        if (!isSharePointHost(hostname)) {
            return false;
        }
        return (pathname.includes('/_layouts/15/embed.aspx') ||
            pathname.includes('/stream.aspx') ||
            pathname.includes('/:v:/') ||
            searchParams.has('UniqueId') ||
            searchParams.has('uniqueId'));
    }
    catch {
        return false;
    }
};
const getSharePointEmbedUrl = (url) => {
    try {
        const parsed = new URL(url);
        if (!isSharePointHost(parsed.hostname)) {
            return null;
        }
        const pathname = parsed.pathname.toLowerCase();
        const search = parsed.searchParams;
        // Already embeddable
        if (pathname.includes('/_layouts/15/embed.aspx') ||
            search.get('action')?.toLowerCase() === 'embedview') {
            return parsed.toString();
        }
        // Common video share link
        if (pathname.includes('/:v:/')) {
            search.set('action', 'embedview');
            return parsed.toString();
        }
        // Direct .mp4 file hosted in SharePoint
        if (pathname.endsWith('.mp4') || pathname.endsWith('.webm') || pathname.endsWith('.ogg') || pathname.endsWith('.mov') || pathname.endsWith('.m4v')) {
            return parsed.toString();
        }
        // Some SharePoint/OneDrive sharing links still preview correctly with embedview
        search.set('action', 'embedview');
        return parsed.toString();
    }
    catch {
        return null;
    }
};
const createSharePointIframe = (doc, embedUrl) => {
    const wrapper = createResponsiveWrapper(doc);
    const iframe = createIframeElement(doc, embedUrl, 'SharePoint video player');
    iframe.setAttribute('allow', 'autoplay; fullscreen; encrypted-media; picture-in-picture');
    wrapper.appendChild(iframe);
    return wrapper;
};
const createSharePointLink = (doc, originalUrl) => {
    const wrapper = doc.createElement('div');
    wrapper.setAttribute('style', 'display: inline-flex; align-items: center; gap: 8px; margin: 16px 0; padding: 12px 20px; background: #f3f2f1; border: 1px solid #e1dfdd; border-radius: 8px;');
    const icon = doc.createElement('span');
    icon.textContent = '▶';
    icon.setAttribute('style', 'font-size: 18px; color: #0078d4;');
    const link = doc.createElement('a');
    link.href = originalUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = 'Open SharePoint video';
    link.setAttribute('style', 'color: #0078d4; font-weight: 600; text-decoration: none; font-size: 15px;');
    wrapper.appendChild(icon);
    wrapper.appendChild(link);
    return wrapper;
};
const trimTrailingPunctuation = (url) => {
    const TRAILING_PUNCTUATION = ')].,!?:;';
    let splitIndex = url.length;
    while (splitIndex > 0 &&
        TRAILING_PUNCTUATION.includes(url[splitIndex - 1])) {
        splitIndex -= 1;
    }
    if (splitIndex === url.length) {
        return {
            cleanUrl: url,
            trailing: ''
        };
    }
    const trailing = url.slice(splitIndex);
    const cleanUrl = url.slice(0, splitIndex);
    return {
        cleanUrl,
        trailing
    };
};
const createMediaNodeForUrl = (doc, rawUrl) => {
    const { cleanUrl } = trimTrailingPunctuation(rawUrl);
    const youtubeEmbedUrl = getYoutubeEmbedUrl(cleanUrl);
    if (youtubeEmbedUrl) {
        return createYoutubeIframe(doc, youtubeEmbedUrl);
    }
    const sharePointEmbedUrl = getSharePointEmbedUrl(cleanUrl);
    if (sharePointEmbedUrl) {
        return createSharePointIframe(doc, sharePointEmbedUrl);
    }
    if (isLikelySharePointVideoUrl(cleanUrl)) {
        return createSharePointIframe(doc, cleanUrl);
    }
    if (isSharePointHostSafe(cleanUrl)) {
        return createSharePointLink(doc, cleanUrl);
    }
    if (isVideoFile(cleanUrl)) {
        return createVideoPlayer(doc, cleanUrl);
    }
    return null;
};
const isSharePointHostSafe = (url) => {
    try {
        return isSharePointHost(new URL(url).hostname);
    }
    catch {
        return false;
    }
};
const replaceAnchorLinks = (doc) => {
    const links = Array.from(doc.querySelectorAll('a'));
    for (const link of links) {
        const href = (link.getAttribute('href') || '').trim();
        const text = (link.textContent || '').trim();
        const candidateUrls = [href, text].filter(Boolean);
        const mediaNode = candidateUrls.reduce((found, url) => found ?? createMediaNodeForUrl(doc, url), null);
        if (mediaNode) {
            link.replaceWith(mediaNode);
        }
    }
};
const replacePlainTextUrlsInTextNode = (doc, textNode) => {
    const text = textNode.textContent || '';
    URL_REGEX.lastIndex = 0;
    if (!URL_REGEX.test(text)) {
        return;
    }
    URL_REGEX.lastIndex = 0;
    const fragment = doc.createDocumentFragment();
    let lastIndex = 0;
    let match;
    while ((match = URL_REGEX.exec(text)) !== null) {
        const matchedUrl = match[0];
        const start = match.index;
        const { cleanUrl, trailing } = trimTrailingPunctuation(matchedUrl);
        if (start > lastIndex) {
            fragment.appendChild(doc.createTextNode(text.slice(lastIndex, start)));
        }
        fragment.appendChild(createMediaNodeForUrl(doc, cleanUrl) ?? doc.createTextNode(cleanUrl));
        if (trailing) {
            fragment.appendChild(doc.createTextNode(trailing));
        }
        lastIndex = start + matchedUrl.length;
    }
    if (lastIndex < text.length) {
        fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
    }
    textNode.parentNode?.replaceChild(fragment, textNode);
};
const replacePlainTextUrls = (doc, node) => {
    if (node.nodeType === Node.TEXT_NODE) {
        replacePlainTextUrlsInTextNode(doc, node);
        return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
    }
    const element = node;
    if (VIDEO_TEXT_SKIP_TAGS.has(element.tagName)) {
        return;
    }
    for (const childNode of Array.from(node.childNodes)) {
        replacePlainTextUrls(doc, childNode);
    }
};
export const convertVideoLinksToPlayers = (htmlContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    replaceAnchorLinks(doc);
    for (const childNode of Array.from(doc.body.childNodes)) {
        replacePlainTextUrls(doc, childNode);
    }
    return doc.body.innerHTML;
};
const SEARCH_HIGHLIGHT_ATTR = 'data-search-highlight';
const unwrapHighlightMarks = (root) => {
    const marks = root.querySelectorAll(`mark[${SEARCH_HIGHLIGHT_ATTR}="true"]`);
    for (const mark of Array.from(marks)) {
        const parent = mark.parentNode;
        if (!parent) {
            continue;
        }
        while (mark.firstChild) {
            parent.insertBefore(mark.firstChild, mark);
        }
        mark.remove();
        parent.normalize();
    }
};
const createHighlightedFragment = (doc, text, regex) => {
    regex.lastIndex = 0;
    if (!regex.test(text)) {
        return null;
    }
    regex.lastIndex = 0;
    const fragment = doc.createDocumentFragment();
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
        const start = match.index;
        const matchedText = match[0];
        if (start > lastIndex) {
            fragment.appendChild(doc.createTextNode(text.slice(lastIndex, start)));
        }
        const mark = doc.createElement('mark');
        mark.setAttribute(SEARCH_HIGHLIGHT_ATTR, 'true');
        mark.textContent = matchedText;
        fragment.appendChild(mark);
        lastIndex = start + matchedText.length;
    }
    if (lastIndex < text.length) {
        fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
    }
    return fragment;
};
const shouldSkipSearchHighlight = (node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) {
        return false;
    }
    return SEARCH_SKIP_TAGS.has(node.tagName);
};
export const highlightSearchInElement = (root, term) => {
    unwrapHighlightMarks(root);
    const search = term.trim();
    if (!search) {
        return;
    }
    const escaped = search.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw `\$&`);
    const regex = new RegExp(escaped, 'gi');
    const doc = root.ownerDocument;
    const highlightTextNode = (textNode) => {
        const text = textNode.textContent || '';
        if (!text) {
            return;
        }
        const fragment = createHighlightedFragment(doc, text, regex);
        if (!fragment) {
            return;
        }
        textNode.parentNode?.replaceChild(fragment, textNode);
    };
    const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            highlightTextNode(node);
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE ||
            shouldSkipSearchHighlight(node)) {
            return;
        }
        for (const childNode of Array.from(node.childNodes)) {
            walk(childNode);
        }
    };
    for (const childNode of Array.from(root.childNodes)) {
        walk(childNode);
    }
};
